import { ReactElement } from 'react'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ProjectFieldDTO } from '@api/fastAPI.schemas'
import { AlertDialog } from '@ui/feedback'
import { fieldTypeLabels } from '../utils'

interface Props {
  fields: ProjectFieldDTO[]
  /** Edit/delete are hidden when absent (read-only members). */
  onEdit?: (field: ProjectFieldDTO) => void
  onDelete?: (field: ProjectFieldDTO) => void
  deletingGuid?: string
}

const ProjectFieldTable = ({ fields, onEdit, onDelete, deletingGuid }: Props): ReactElement => {
  if (!fields.length) {
    return (
      <Typography variant="body2" color="text.secondary" data-cy="project-fields-empty">
        Noch keine Projektdatenfelder vorhanden.
      </Typography>
    )
  }

  return (
    <Table size="small" data-cy="project-fields-table">
      <TableHead>
        <TableRow>
          <TableCell>Titel</TableCell>
          <TableCell>Platzhalter</TableCell>
          <TableCell>Typ</TableCell>
          <TableCell>Beschreibung</TableCell>
          {(onEdit || onDelete) && <TableCell align="right" />}
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map(field => (
          <TableRow key={field.guid} data-cy={`project-field-row-${field.template_key}`}>
            <TableCell>{field.title}</TableCell>
            <TableCell>
              <code>{`{{project_data.${field.template_key}}}`}</code>
            </TableCell>
            <TableCell>
              {fieldTypeLabels[field.field_type]}
              {field.field_type === 'select' && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {field.options.map(option => option.label).join(', ')}
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'pre-line' }}>{field.description}</TableCell>
            {(onEdit || onDelete) && (
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end">
                  {onEdit && (
                    <Tooltip title="Bearbeiten">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(field)}
                        data-cy={`project-field-edit-${field.template_key}`}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="Löschen">
                      <span>
                        <AlertDialog
                          title="Projektdatenfeld löschen?"
                          text={`Das Feld „${field.title}“ verschwindet aus dem Projektformular aller Projekte der Organisation, und der Platzhalter {{project_data.${field.template_key}}} bleibt in den Dokumenten leer.`}
                          confirmLabel="Löschen"
                          onConfirm={() => onDelete(field)}
                        >
                          <IconButton
                            size="small"
                            disabled={deletingGuid === field.guid}
                            data-cy={`project-field-delete-${field.template_key}`}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </AlertDialog>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default ProjectFieldTable
